import { Col, Form as AntForm, Form, Row, Tabs } from "antd";
import type { FormInstance } from "antd/es/form/Form";
import { Md5 } from "md5-typescript";
import { type FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ConditionalWrapper, isFieldVisible } from "../../../../components/Form/components/ConditionalWrapper/ConditionalWrapper";
import { getValidationRules } from "../../../../components/Form/components/Validations/ValidationFieldsFactory";
import { createHorizontalFormField } from "../../../../components/Form/formFieldLabelPositioning/Horizontal/CreateHorizontalFormField";
import type { Attributes, FormConfiguration, FormField } from "../../../../components/Form/types/types";
import { FormComponent } from "../../../../components/Form/types/types";
import { canAccess } from "../../../../core/router/auth/permissions";
import type { GenericObject } from "../../../../core/types";
import type { Entity } from "../../../../core/types/fe";

export const fullWidthComponents = [
    FormComponent.DYNAMIC_PROPERTY_GROUP_PROPERTIES_TABLE,
    FormComponent.CONTRACTS_TERMINALS_TABLE,
    FormComponent.CONTRACTS_APP_PROFILES_TABLE,
    FormComponent.VIRTUAL_ACQUIRERS_PHYSICAL_ACQUIRERS_TABLE,
    FormComponent.CONTRACTS_VIRTUAL_APP_PROFILES_TABLE,
    FormComponent.APPLICATIONS_PROFILE_CARD_DEFINITIONS_TABLE,
    FormComponent.ACQUIRERS_OPERATORS_TABLE,
    FormComponent.SITES_CONTRACTS_TABLE,
    FormComponent.APPLICATIONS_PROFILE_PROMPT_DATA,
    FormComponent.DYNAMIC_PROPERTIES,
    FormComponent.DATA_GROUP_USERS_TABLE,
    FormComponent.DATA_GROUP_IDM_USERS_TABLE,
    FormComponent.SITES_TABLE_VIEW,
    FormComponent.VIRTUAL_SITES_TABLE,
    FormComponent.PARAMETERS_TABLE,
    FormComponent.TERMINAL_CONTRACTS_TABLE,
    FormComponent.TERMINAL_HOST_TERMINAL,
    FormComponent.CARD_DEFINITIONS_TABLE,
    FormComponent.CARD_ATTRIBUTES_TABLE,
    FormComponent.EMV_KERNEL_EMV_CONFIGURATIONS_TABLE,
    FormComponent.EMV_KERNEL_CA_PUBLIC_KEYS_TABLE,
    FormComponent.EMV_KERNEL_AID_PREFERENCES_TABLE,
    FormComponent.EMV_KERNEL_AID_PREFERENCES_CLESS_TABLE,
    FormComponent.ACQUIRERS_CONTRACTS_TABLE,
    FormComponent.TERMINAL_TO_CAMPAIGN_TABLE,
    FormComponent.CAMPAIGN_TERMINALS_SELECTABLE_TABLE,
    FormComponent.APPLICATION_CONFIGURATIONS_VALIDATIONS_EDIT,
    FormComponent.VAT_CODE_VAT_VALUES_TABLE,
    FormComponent.PRODUCT_PROFILE_PRODUCTS,
    FormComponent.CARD_COMBINATIONS,
    FormComponent.CARD_TYPES_MAPPING_TABLE,
    FormComponent.SITES_VIRTUAL_SITES_TABLE,
    FormComponent.ORG_UNITS_PRODUCTS_PROFILE,
];

interface GenericFormProps {
    initialData?: Entity;
    formConfig: FormConfiguration;
    onFormUpdate: Function;
    form: FormInstance;
}

export const GenericForm: FC<GenericFormProps> = ({ form, initialData, formConfig, onFormUpdate }) => {
    const { t } = useTranslation();
    const resourceName = formConfig.resource;

    const [formInstance, setFormInstance] = useState<FormInstance | null>(null);
    useEffect(() => {
        if (setFormInstance) setFormInstance(form);
    }, [form, setFormInstance]);

    useEffect(() => {
        form.resetFields();
        form.setFieldsValue(initialData);
    }, [form, initialData]);

    const hasVisibility = useCallback(
        (field?: Attributes) => {
            return field && isFieldVisible(formInstance?.getFieldsValue(), field);
        },
        [formInstance],
    );

    const onValuesChangeCallback = (values: GenericObject) => {
        onFormUpdate(values);
    };

    const formTabs = formConfig?.tabs.map((tab) => {
        // If the user has not reading access to this tab it will not even be rendered.
        const userHasReadAccess = canAccess(resourceName, "canRead");
        if (!userHasReadAccess) return null;

        const key = `${formConfig.resource}_${tab.name}`;

        const getFormFieldControl = (field: FormField) => {
            // If the user has not writing access on this tab the fields inside this tab are disabled (read-only).
            const formIsReadOnly = !canAccess(resourceName, "canWrite");
            // Also, if the field can be disabled by a higher rule, set in field's disableIf attribute.
            const disabledByOtherField = field.disableIf ? !!formInstance?.getFieldValue(field.disableIf) : false;
            const readOnly = formIsReadOnly || (field.attributes?.readOnly ?? false) || disabledByOtherField;

            const rules = getValidationRules(field.attributes?.validationRules);
            const fieldComponent = createHorizontalFormField({
                field,
                readOnly,
                rules,
                changeCallback: onValuesChangeCallback,
            });

            // find sibling associated RO field in config by readOnlyName from field attributes
            const readOnlyFieldConfig = tab.fields.find((fieldConfig) => fieldConfig.name === field.attributes?.readOnlyName);
            const formReadOnlyField = readOnlyFieldConfig && getFormFieldControl(readOnlyFieldConfig);

            const isVisible = hasVisibility(field.attributes) && !field.component.startsWith("Hidden");
            const isFullWidth = fullWidthComponents.filter((c) => c === field?.component).length > 0;

            const fieldRow = isFullWidth ? (
                <Row key={`${fieldComponent.props.name}-row`} hidden={!isVisible}>
                    <Col span={24}>{fieldComponent}</Col>
                </Row>
            ) : (
                <Row key={`${fieldComponent.props.name}-row`} gutter={24} hidden={!isVisible}>
                    <Col span={14}>{fieldComponent}</Col>
                    <Col span={10}>{formReadOnlyField}</Col>
                </Row>
            );

            const wrapperKey = field.attributes?.condition ? Md5.init(`${field.name}${field.attributes?.condition ?? ""}`) : field.name;
            return (
                <ConditionalWrapper key={wrapperKey} fieldConfig={field}>
                    {fieldRow}
                </ConditionalWrapper>
            );
        };

        // const hasVisibleFields = tab.fields.some(field => isFieldVisible(formInstance?.getFieldsValue(), field.attributes));

        return (
            <Tabs.TabPane key={key} tab={t(tab.name)} forceRender>
                {tab.fields.map((field) => {
                    // Skip ReadOnlyCheckbox fields from rendering in the form. They are handled in the FormFieldRender component.
                    if (field.component === FormComponent.READ_ONLY_CHECK) {
                        return null;
                    }

                    if (field.attributes?.visible === false) {
                        return null;
                    }

                    return getFormFieldControl(field);
                })}
            </Tabs.TabPane>
        );
    });

    return (
        <AntForm
            data-name={resourceName}
            initialValues={initialData}
            form={form}
            name={resourceName}
            onValuesChange={onValuesChangeCallback}
            autoComplete="off"
        >
            <Tabs>{formTabs}</Tabs>
            <Form.Item key={`${initialData?.id}-Id`} name="id" hidden>
                <input type="hidden" value={initialData?.id} />
            </Form.Item>
        </AntForm>
    );
};
